// Strips credentials and personal data out of a Datadog log event before it
// leaves the device.
//
// Why this exists, rather than fixing the call sites: the console interceptor
// in datadog.js forwards every argument through JSON.stringify, and an Axios
// error carries config.headers.Authorization and config.data by construction.
// So `console.error('Save failed:', err)` ships an access token without the
// author of that line doing anything wrong. A 30-day Datadog audit found
// ~18,000 log events across HOGC services carrying a live JWT this way.
//
// Wiring this into `beforeSend` covers both paths into Datadog Logs — the
// console interceptor and the SDK's own `forwardErrorsToLogs` — in one place,
// and covers call sites nobody has written yet.
//
// Fixing it here does not make the call sites correct: the data is still
// assembled in memory. It stops it being transmitted, stored and searchable.

// A JWT: three base64url segments, the first starting `eyJ` (base64 for `{"`).
// Matches a bare token, one inside `Bearer …`, and one nested in a stringified
// request config.
const JWT = /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g;

// Any Authorization header value, whether or not it holds a JWT.
const AUTH_HEADER = /("?[Aa]uthorization"?\s*[:=]\s*"?)(Bearer\s+)?[A-Za-z0-9._\-~+/]+=*/g;

// JSON key/value pairs whose value is personal data or a secret. Matched as a
// pair so the value goes and the key stays — a log that says the field was
// present, without its contents, is still useful when reading a trace back.
//
// The quotes may be escaped, any number of times: an Axios error holds its
// request body as a JSON string, so a stringified error carries
// `\"pin\":\"1234\"`. Until Oct 2026 only bare quotes matched, and members'
// giving PINs reached Datadog inside `Landing: Verify error` lines. The
// backslashes in front of the key are captured and repeated in the
// replacement, so the escaping level of the surrounding text is kept.
const SENSITIVE_PAIR = new RegExp(
  String.raw`(\\*)"(` +
    [
      // credentials
      'token',
      'accessToken',
      'access_token',
      'refreshToken',
      'refresh_token',
      'idToken',
      'id_token',
      'password',
      'newPassword',
      'pin',
      'otp',
      'authorization',
      // identity
      'authSub',
      'auth0Id',
      'auth0IdPrefixed',
      'email',
      'originalEmail',
      'otpEmail',
      // person
      'fullName',
      'familyName',
      'givenName',
      'preferredName',
      'name',
      'dateOfBirth',
      'dob',
      'partialNric',
      'personalId',
      'mobileNumber',
      'localMobileNumber',
      'originalMobile',
      'otpMobileNumber',
      // financial
      'maskedBankAccNumber',
      'eGiroMandateId',
    ].join('|') +
    String.raw`)\\*"\s*:\s*(?:\\*"(?:[^"\\]|\\+[^"\\])*\\*"|\d+|true|false|null)`,
  'gi',
);

// Signed or identifying query parameters in a URL: the Capgo updater logs its
// bundle download link, `?key=<signed key>&device_id=<id>`, when a download
// fails.
const URL_SECRET_PARAM = /([?&](?:key|token|signature|sig|x-amz-signature|device_id)=)[^&\s"']+/gi;

const REDACTED = '[REDACTED]';

// Replaces sensitive substrings in one string. Order matters: tokens first, so
// a JWT sitting inside an Authorization header is gone before the broader
// header rule runs.
export function scrubString(value) {
  if (typeof value !== 'string' || value.length === 0) return value;
  return value
    .replace(JWT, REDACTED)
    .replace(AUTH_HEADER, `$1${REDACTED}`)
    .replace(URL_SECRET_PARAM, `$1${REDACTED}`)
    .replace(
      SENSITIVE_PAIR,
      (_match, esc, key) => `${esc}"${key}${esc}":${esc}"${REDACTED}${esc}"`,
    );
}

// Walks an event and scrubs every string in it. Depth-limited and
// cycle-guarded: a Datadog event can carry a serialised error whose shape we
// do not control, and this runs on every log line.
export function scrubEvent(value, depth = 0, seen = new WeakSet()) {
  if (depth > 8) return value;
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = scrubEvent(value[i], depth + 1, seen);
    }
    return value;
  }

  for (const key of Object.keys(value)) {
    value[key] = scrubEvent(value[key], depth + 1, seen);
  }
  return value;
}

// `beforeSend` for datadogLogs.init. Never throws and never drops an event: a
// scrubber that breaks logging is worse than the leak it was added to fix, so
// on any unexpected failure the event is sent with its message blanked rather
// than sent raw.
export function scrubBeforeSend(event) {
  try {
    scrubEvent(event);
  } catch {
    if (event && typeof event === 'object') {
      event.message = '[REDACTED: scrubber failed]';
    }
  }
  return true;
}

// Everything after `?` or `#`. RUM URLs are only useful down to the path; the
// query is where prefilled form iframes carry a member's name, phone, NRIC
// fragment and date of birth, and where auth redirects carry codes.
export function stripUrlQuery(url) {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

// `beforeSend` for datadogRum.init. Strips the query and fragment from every
// URL RUM records (views, referrers, resources, a failing request on an error)
// and runs error text through the same scrubber as Datadog Logs. Same contract
// as scrubBeforeSend: never throws, never drops an event.
export function scrubRumBeforeSend(event) {
  try {
    if (event?.view) {
      event.view.url = stripUrlQuery(event.view.url);
      event.view.referrer = stripUrlQuery(event.view.referrer);
    }
    if (event?.resource) {
      event.resource.url = stripUrlQuery(event.resource.url);
    }
    if (event?.error) {
      if (event.error.resource) {
        event.error.resource.url = stripUrlQuery(event.error.resource.url);
      }
      event.error.message = scrubString(event.error.message);
      event.error.stack = scrubString(event.error.stack);
    }
  } catch {
    // A half-scrubbed event is still better than none reaching Datadog.
  }
  return true;
}
